import { useRef, useState } from 'react';

import _ from 'lodash';
import toast from 'react-hot-toast';
import { DndContext, DragOverlay } from '@dnd-kit/core';

import { LoanField } from '__generated__/globalTypes';
import FieldsList from '../fieldMapping/FieldsList';
import FileHeadersList, { HeaderPillPreview } from '../fieldMapping/FileHeadersList';
import { useHeaderMappingDnd } from '../fieldMapping/useHeaderMappingDnd';
import { MappingCardFile } from '../fieldMapping/__generated__/MapRequiredFieldsCard';
import {
  FieldsMap,
  countUnmappedRequiredFields,
  getFileFieldReqs,
  mappingArrayToMap,
} from '../fieldMapping/MapRequiredFieldsCard';
import { tapestryErrorMessage } from './errorMessages';
import { RoleGatedButton } from './RoleGatedButton';
import { useSubmitMappingCorrections } from './useSubmitMappingCorrections';
import { extractTapestryErrorCode } from './useTapestryJobActions';

// E-4220 tells the reviewer which column to drag Account Id onto; the 4 s default is too
// short to read it and act before it disappears.
const INSTRUCTION_TOAST_DURATION_MS = 10_000;

const GENERIC_SUBMIT_ERROR = 'Could not submit the mapping. Please try again.';
const ACCOUNT_ID_NOT_ON_4C_ASSET_ID = 'E-4220';

interface Props {
  selectedFile: MappingCardFile;
  /**
   * Called after corrections submit so the caller can refetch job status.
   * May return a promise (the refetch) so we can re-enable the gate if it fails.
   */
  onSubmitted: () => void | Promise<unknown>;
}

/**
 * The Tapestry mapping gate (current_stage === WAITING_FOR_MAPPING). The
 * pipeline has already auto-mapped columns; this lets a portfolio or deal manager
 * correct the mapping and resume the run via `submitMappingCorrections`.
 *
 * Reuses the legacy drag-and-drop mapping UI (FieldsList / FileHeadersList) and
 * the shared required-field rules, but deliberately omits LLM auto-map (the
 * pipeline already did that) and the legacy `submitFileHeaderMap` / ingest
 * actions, which don't apply to the pipeline.
 */
export const TapestryMappingGate = ({ selectedFile, onSubmitted }: Props): JSX.Element => {
  const { requiredFields, optionalFields } = getFileFieldReqs(selectedFile);

  const [fieldsMap, setFieldsMap] = useState<FieldsMap>(
    mappingArrayToMap(selectedFile.field_mapping),
  );

  const { submitMappingCorrections, loading } = useSubmitMappingCorrections();
  // Latches true on a successful submit and stays true until this component
  // unmounts (the gate disappears once current_stage advances past
  // WAITING_FOR_MAPPING). Apollo's `loading` drops as soon as the mutation
  // resolves — before the parent's refetch updates the stage — so without this
  // the button would briefly re-enable and allow a duplicate submission.
  const [submitted, setSubmitted] = useState(false);
  // Synchronous re-entry guard: `submitted`/`loading` only disable the button on
  // the next render, so a double-click in the same tick could fire two
  // submitMappingCorrections calls. The ref blocks the second immediately
  // (matches the cancel/confirm/reject/retry handlers).
  const submitInFlight = useRef(false);

  const unmappedHeaders =
    selectedFile.headers?.filter((header) => !_.includes(_.values(fieldsMap), header)) || [];

  const unmappedRequiredFieldCount = countUnmappedRequiredFields(requiredFields, fieldsMap);
  const allRequiredMapped = unmappedRequiredFieldCount === 0;
  const locked = loading || submitted;

  const onMap = (fieldId: string, header: string) => {
    if (locked) {
      return;
    }
    setFieldsMap((prev) => ({ ...prev, [fieldId]: header }));
  };

  const onUnmap = (fieldId: LoanField) => {
    if (locked) {
      return;
    }
    setFieldsMap((prev) => {
      const updated = { ...prev };
      delete updated[fieldId];
      return updated;
    });
  };

  const { sensors, activeHeader, handleDragStart, handleDragEnd, handleDragCancel } =
    useHeaderMappingDnd(onMap);

  const onSubmit = async () => {
    if (submitInFlight.current || locked) {
      return;
    }
    submitInFlight.current = true;
    setSubmitted(true);
    try {
      await submitMappingCorrections(selectedFile.id, fieldsMap);
    } catch (error) {
      // Submit itself failed — re-enable so the user can retry.
      submitInFlight.current = false;
      setSubmitted(false);
      const code = extractTapestryErrorCode(error);
      if (code === ACCOUNT_ID_NOT_ON_4C_ASSET_ID) {
        toast.error(tapestryErrorMessage(code, GENERIC_SUBMIT_ERROR), {
          duration: INSTRUCTION_TOAST_DURATION_MS,
        });
      } else {
        toast.error(GENERIC_SUBMIT_ERROR);
      }
      return;
    }
    try {
      // On success the gate unmounts once the refetched stage advances past
      // WAITING_FOR_MAPPING, so we keep `submitted` set on the happy path.
      await onSubmitted();
    } catch {
      // Submit succeeded but the status refresh failed; re-enable so the user
      // isn't stuck on a permanently-disabled button (re-submitting is
      // idempotent and triggers another refresh).
      submitInFlight.current = false;
      setSubmitted(false);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div
        className="rounded border border-accent-muted bg-background-canvas p-5 text-sm"
        data-testid="tapestry-mapping-gate"
      >
        <h2 className="mb-2.5 text-lg text-gray-600">Review column mapping</h2>
        <p className="my-1 text-xs text-gray-500">
          We auto-mapped your tape’s columns to ORSNN fields. Drag to correct any that look wrong,
          then submit to continue processing.
        </p>
        <div className="mt-2 flex h-[640px] justify-center gap-8 text-sm">
          <FieldsList
            requiredFields={requiredFields}
            optionalFields={optionalFields}
            map={fieldsMap}
            onUnmap={onUnmap}
            disabled={locked}
          />
          <FileHeadersList unmappedHeaders={unmappedHeaders} disabled={locked} />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-end gap-3 px-3 py-3">
          {unmappedRequiredFieldCount > 0 && (
            <span className="text-xs text-gray-500" data-testid="tapestry-mapping-remaining">
              {unmappedRequiredFieldCount} required field
              {unmappedRequiredFieldCount === 1 ? '' : 's'} remaining
            </span>
          )}
          <RoleGatedButton
            label="Submit mapping"
            data-testid="tapestry-mapping-submit-button"
            disabled={!allRequiredMapped || locked}
            onClick={() => void onSubmit()}
          >
            Submit mapping
          </RoleGatedButton>
        </div>
      </div>
      <DragOverlay>{activeHeader ? <HeaderPillPreview label={activeHeader} /> : null}</DragOverlay>
    </DndContext>
  );
};
